import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import AppImage from '../../../components/AppImage';
import { X, Package, User, MapPin, CreditCard, Truck, CheckCircle, Clock, AlertCircle, Edit2, Trash2, Save, FileText, Building2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

// Simple toast helper (inline, no external dep needed)
const showToast = (message, type = 'success') => {
  const el = document.createElement('div');
  el.textContent = message;
  el.style.cssText = `
    position:fixed;bottom:24px;right:24px;z-index:9999;
    padding:12px 20px;border-radius:8px;font-size:14px;font-weight:600;
    color:#fff;box-shadow:0 4px 12px rgba(0,0,0,0.15);
    background:${type === 'success' ? '#16a34a' : '#dc2626'};
    transition:opacity 0.3s;
  `;
  document.body?.appendChild(el);
  setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el?.remove(), 300); }, 3000);
};

export default function OrderDetailsModal({ order: initialOrder, onClose, onUpdateStatus }) {
  const [order, setOrder] = useState(initialOrder);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [editingTotal, setEditingTotal] = useState(false);
  const [newTotal, setNewTotal] = useState('');
  const [savingTotal, setSavingTotal] = useState(false);
  const [removingItemId, setRemovingItemId] = useState(null);

  // Always fetch fresh items from DB on mount — guarantees icons appear
  useEffect(() => {
    if (!initialOrder?.id) return;
    const fetchItems = async () => {
      setItemsLoading(true);
      try {
        const { data, error } = await supabase?.from('order_items')?.select('*, products(id, title, images, image_urls, price)')?.eq('order_id', initialOrder?.id);
        if (error) throw error;
        setItems(data || []);
      } catch (err) {
        console.error('OrderDetailsModal: failed to fetch items', err);
        // Fallback to whatever was passed in
        const fallback = initialOrder?.items || initialOrder?.order_items || [];
        setItems(fallback);
      } finally {
        setItemsLoading(false);
      }
    };
    fetchItems();
  }, [initialOrder?.id]);

  if (!order) return null;

  const handleEditTotal = () => {
    setNewTotal(parseFloat(order?.total_amount)?.toFixed(2));
    setEditingTotal(true);
  };

  const handleSaveTotal = async () => {
    const parsed = parseFloat(newTotal?.toString()?.replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      showToast('Geçerli bir tutar girin.', 'error');
      return;
    }
    setSavingTotal(true);
    try {
      const { error } = await supabase?.from('orders')?.update({ total_amount: parsed })?.eq('id', order?.id);
      if (error) throw error;
      setOrder(prev => ({ ...prev, total_amount: parsed }));
      setEditingTotal(false);
      showToast(`Toplam tutar ${parsed?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL olarak güncellendi.`);
    } catch (err) {
      showToast('Hata: ' + err?.message, 'error');
    } finally {
      setSavingTotal(false);
    }
  };

  const handleRemoveItem = async (itemId, itemTitle) => {
    if (!window.confirm(`"${itemTitle}" kalemini siparişten kaldırmak istediğinize emin misiniz?`)) return;
    setRemovingItemId(itemId);
    try {
      const { error } = await supabase?.from('order_items')?.delete()?.eq('id', itemId);
      if (error) throw error;
      setItems(prev => prev?.filter(i => i?.id !== itemId));
      showToast(`"${itemTitle}" siparişten kaldırıldı.`);
    } catch (err) {
      showToast('Kalem kaldırılamadı: ' + err?.message, 'error');
    } finally {
      setRemovingItemId(null);
    }
  };

  const statusOptions = [
    { value: 'pending', label: 'Beklemede', color: 'yellow' },
    { value: 'confirmed', label: 'Onaylandı', color: 'blue' },
    { value: 'processing', label: 'İşleniyor', color: 'purple' },
    { value: 'shipped', label: 'Kargoya Verildi', color: 'indigo' },
    { value: 'delivered', label: 'Teslim Edildi', color: 'green' },
    { value: 'cancelled', label: 'İptal Edildi', color: 'red' }
  ];

  const paymentStatus = order?.payment_status || order?.paymentStatus || 'pending';
  const paymentMethod = order?.payment_method || order?.paymentMethod || 'credit_card';

  const handleMarkAsPaid = async () => {
    setMarkingPaid(true);
    try {
      const { error } = await supabase?.from('orders')?.update({ payment_status: 'paid' })?.eq('id', order?.id);
      if (!error) {
        setOrder(prev => ({ ...prev, payment_status: 'paid', paymentStatus: 'paid' }));
        showToast('Sipariş başarıyla ödendi olarak işaretlendi.');
        if (onUpdateStatus) onUpdateStatus(order?.id, order?.status);
      } else {
        showToast('Hata: ' + error?.message, 'error');
      }
    } catch (err) {
      showToast('Beklenmeyen hata: ' + err?.message, 'error');
    } finally {
      setMarkingPaid(false);
    }
  };

  const renderPaymentStatusBadge = () => {
    if (paymentStatus === 'paid' || paymentStatus === 'completed') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-green-100 text-green-800 font-semibold">
          <CheckCircle className="h-4 w-4" />
          Ödendi
        </span>
      );
    }
    if (paymentStatus === 'pending' || paymentStatus === 'unpaid') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-amber-100 text-amber-800 font-semibold">
          <Clock className="h-4 w-4" />
          Ödeme Bekleniyor
        </span>
      );
    }
    if (paymentStatus === 'failed') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-red-100 text-red-800 font-semibold">
          <AlertCircle className="h-4 w-4" />
          Ödeme Başarısız
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-gray-100 text-gray-700 font-medium">
        {paymentStatus}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Sipariş Detayları</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Order Info */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Sipariş ID</p>
              <p className="font-semibold text-gray-900">{order?.id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Sipariş Tarihi</p>
              <p className="font-semibold text-gray-900">
                {new Date(order?.created_at)?.toLocaleDateString('tr-TR', {
                  year: 'numeric', month: 'long', day: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                })}
              </p>
            </div>
          </div>

          {/* Payment Status */}
          <div className={`border-2 rounded-lg p-4 ${
            (paymentStatus === 'paid' || paymentStatus === 'completed')
              ? 'border-green-200 bg-green-50'
              : paymentStatus === 'failed' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Ödeme Durumu</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {renderPaymentStatusBadge()}
                  {order?.payment_status !== 'paid' && order?.payment_status !== 'completed' && (
                    <button
                      onClick={async () => {
                        await supabase?.from('orders')?.update({ payment_status: 'paid' })?.eq('id', order?.id);
                        window.location?.reload();
                      }}
                      className="ml-3 bg-green-500 text-white text-xs px-3 py-1 rounded hover:bg-green-600 shadow"
                    >
                      ✓ Ödendi İşaretle
                    </button>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 mb-1">Ödeme Yöntemi</p>
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full font-medium ${
                  paymentMethod === 'bank_transfer' ? 'bg-indigo-100 text-indigo-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {paymentMethod === 'bank_transfer' ? 'EFT / Havale' : 'Kredi Kartı'}
                </span>
              </div>
            </div>
            {paymentStatus === 'pending' && paymentMethod === 'credit_card' && (
              <p className="text-xs text-amber-700 mt-2">
                ⚠️ Bu sipariş 3D Secure doğrulaması tamamlanmadan oluşturuldu. Ödeme henüz alınmadı.
              </p>
            )}
          </div>

          {/* Customer Info */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <User className="h-5 w-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Müşteri Bilgileri</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Ad Soyad</p>
                <p className="font-medium text-gray-900">
                  {(() => {
                    let customerName = "İsimsiz Müşteri";
                    try {
                      if (order?.profiles?.full_name) {
                        customerName = order?.profiles?.full_name;
                      } else if (order?.full_name && order?.full_name?.trim() !== '') {
                        customerName = order?.full_name;
                      } else if (order?.user?.full_name) {
                        customerName = order?.user?.full_name;
                      } else {
                        let addr = typeof order?.shipping_address === 'string'
                          ? JSON.parse(order?.shipping_address)
                          : (order?.shipping_address || {});
                        customerName = addr?.adSoyad || addr?.fullName || addr?.name || addr?.full_name || "İsimsiz Müşteri";
                      }
                    } catch (e) {
                      console.error("Error parsing name", e);
                    }
                    return customerName;
                  })()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">E-posta</p>
                <p className="font-medium text-gray-900">{order?.user?.email || order?.guest_email || '-'}</p>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Teslimat Adresi</h3>
            </div>
            <div className="space-y-2">
              {(() => {
                const isGuest = !order?.user_id;
                let addr = (() => {
                  try {
                    return typeof order?.shipping_address === 'string'
                      ? JSON.parse(order?.shipping_address)
                      : (order?.shipping_address || {});
                  } catch (e) { return {}; }
                })();
                return (
                  <>
                    <p className="font-bold text-gray-800">
                      {order?.full_name ||
                       order?.profiles?.full_name ||
                       addr?.ContactName ||
                       addr?.adSoyad ||
                       "İsimsiz Müşteri"}
                      {isGuest && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                          Misafir
                        </span>
                      )}
                    </p>
                    <p className="text-gray-600">{addr?.address}</p>
                    <p className="text-gray-600">{addr?.city} {addr?.postalCode}</p>
                    <p className="text-gray-600">Tel: {addr?.phone}</p>
                    <p><strong>E-posta:</strong> {order?.email || order?.profiles?.email || "Belirtilmedi"}</p>
                  </>
                );
              })()}
            </div>
          </div>

          {/* ─── FATURA BİLGİLERİ CARD ─── */}
          {(() => {
            const invoiceType = order?.billing_type || order?.invoice_type || 'individual';
            const isKurumsal = invoiceType === 'CORPORATE' || invoiceType === 'corporate';
            const companyName = order?.company_name || '';
            const taxOffice = order?.tax_office || '';
            const taxId = order?.tax_id || '';
            const billingAddress = order?.billing_address || '';

            return (
              <div className={`border-2 rounded-lg p-4 ${isKurumsal ? 'border-purple-200 bg-purple-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-4">
                  {isKurumsal ? (
                    <Building2 className="h-5 w-5 text-purple-600" />
                  ) : (
                    <FileText className="h-5 w-5 text-gray-600" />
                  )}
                  <h3 className="font-semibold text-gray-900">Fatura Bilgileri</h3>
                  <span className={`ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    isKurumsal
                      ? 'bg-purple-100 text-purple-800' :'bg-gray-100 text-gray-700'
                  }`}>
                    {isKurumsal ? '🏢 Kurumsal' : '👤 Bireysel'}
                  </span>
                </div>

                {isKurumsal ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {companyName && (
                      <div>
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Şirket Adı</p>
                        <p className="font-semibold text-gray-900 mt-0.5">{companyName}</p>
                      </div>
                    )}
                    {taxOffice && (
                      <div>
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Vergi Dairesi</p>
                        <p className="font-semibold text-gray-900 mt-0.5">{taxOffice}</p>
                      </div>
                    )}
                    {taxId && (
                      <div>
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Vergi No (VKN)</p>
                        <p className="font-mono font-semibold text-gray-900 mt-0.5">{taxId}</p>
                      </div>
                    )}
                    {billingAddress && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Fatura Adresi</p>
                        <p className="font-medium text-gray-900 mt-0.5 whitespace-pre-line">{billingAddress}</p>
                      </div>
                    )}
                    {!companyName && !taxOffice && !taxId && !billingAddress && (
                      <p className="text-sm text-gray-500 italic sm:col-span-2">Kurumsal fatura bilgileri girilmemiş.</p>
                    )}
                  </div>
                ) : (
                  <div>
                    {taxId ? (
                      <div>
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">TC Kimlik No</p>
                        <p className="font-mono font-semibold text-gray-900 mt-0.5">{taxId}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Bireysel fatura — TC Kimlik No girilmemiş.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ─── ORDER ITEMS ─── */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Package className="h-5 w-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Sipariş İçeriği</h3>
            </div>

            {itemsLoading ? (
              <p className="text-sm text-gray-500 text-center py-4">Kalemler yükleniyor...</p>
            ) : items?.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Sipariş kalemi bulunamadı.</p>
            ) : (
              <div className="space-y-3">
                {items?.map(item => {
                  const unitPrice = parseFloat(item?.price_at_purchase || item?.priceAtPurchase || 0);
                  const lineTotal = unitPrice * (item?.quantity || 1);
                  const productData = item?.products || item?.product || {};
                  const itemTitle = productData?.title || item?.title || 'Ürün';
                  const itemImage = productData?.images?.[0] || productData?.image_urls?.[0] || '/assets/images/no_image.png';

                  return (
                    <div
                      key={item?.id}
                      className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-100"
                    >
                      {/* Product Image */}
                      <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                        <AppImage
                          src={itemImage}
                          alt={itemTitle}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{itemTitle}</p>
                        <p className="text-sm text-gray-500">Adet: {item?.quantity || 1}</p>
                        <p className="text-xs text-gray-400">{unitPrice?.toFixed(2)} TL/adet</p>
                      </div>

                      {/* Line Total */}
                      <div className="text-right flex-shrink-0">
                        <p className="font-semibold text-gray-900">{lineTotal?.toFixed(2)} TL</p>
                      </div>

                      {/* ── TRASH ICON — always visible, no conditions ── */}
                      <button
                        onClick={() => handleRemoveItem(item?.id, itemTitle)}
                        disabled={removingItemId === item?.id}
                        title="Bu kalemi sil"
                        className="flex-shrink-0 ml-2 p-2 rounded-lg bg-red-50 hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-40"
                      >
                        {removingItemId === item?.id ? (
                          <span className="text-xs text-red-400 font-bold">...</span>
                        ) : (
                          <Trash2 size={16} className="text-red-500" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Payment Info */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="h-5 w-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Ödeme Bilgileri</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Ara Toplam</span>
                <span className="font-medium text-gray-900">
                  {parseFloat(order?.total_amount)?.toFixed(2)} TL
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Kargo</span>
                <span className="font-medium text-green-600">Ücretsiz</span>
              </div>

              {/* ── TOTAL ROW with PENCIL ICON — always visible ── */}
              <div className="flex justify-between pt-2 border-t border-gray-200 items-center">
                <span className="font-semibold text-gray-900">Toplam Tutar</span>

                {editingTotal ? (
                  /* Edit mode */
                  (<div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={newTotal}
                      onChange={e => setNewTotal(e?.target?.value)}
                      className="w-36 border-2 border-blue-400 rounded-lg px-3 py-1.5 text-right font-bold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                      autoFocus
                    />
                    <span className="text-gray-600 text-sm font-medium">TL</span>
                    <button
                      onClick={handleSaveTotal}
                      disabled={savingTotal}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors"
                      title="Kaydet"
                    >
                      <Save className="h-4 w-4" />
                      Kaydet
                    </button>
                    <button
                      onClick={() => setEditingTotal(false)}
                      className="p-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
                      title="İptal"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>)
                ) : (
                  /* Display mode — pencil always visible */
                  (<div className="flex items-center gap-2">
                    <span className="font-bold text-blue-600 text-lg">
                      {parseFloat(order?.total_amount)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                    </span>
                    {/* ── PENCIL ICON — always rendered, no conditions ── */}
                    <button
                      onClick={handleEditTotal}
                      className="p-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-500 hover:text-blue-700 rounded-lg transition-colors"
                      title="Toplam tutarı düzenle"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  </div>)
                )}
              </div>

              <div className="flex justify-between pt-2 items-center">
                <span className="text-gray-600 font-medium">Ödeme Durumu</span>
                {renderPaymentStatusBadge()}
              </div>
              {order?.payment_transaction_id && (
                <div className="flex justify-between">
                  <span className="text-gray-600">İşlem ID</span>
                  <span className="font-mono text-sm text-gray-900">{order?.payment_transaction_id}</span>
                </div>
              )}
            </div>
          </div>

          {/* Order Status */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Truck className="h-5 w-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Sipariş Durumu (Kargo/Teslimat)</h3>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={order?.status}
                onChange={(e) => onUpdateStatus?.(order?.id, e?.target?.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {statusOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
              <Button onClick={() => onUpdateStatus?.(order?.id, order?.status)}>
                Durumu Güncelle
              </Button>
            </div>
          </div>

          {/* İşlem Merkezi */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-4">İşlem Merkezi</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <Truck className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-blue-800 mb-1">Kargo Bilgileri</p>
                <p className="text-xs text-blue-600">
                  {order?.tracking_number ? `Takip: ${order?.tracking_number}` : 'Henüz kargo yok'}
                </p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-green-800 mb-1">Teslimat Onayı</p>
                <p className="text-xs text-green-600">
                  {order?.status === 'delivered' ? '✅ Teslim Edildi' : 'Beklemede'}
                </p>
              </div>
            </div>
          </div>

          {/* Ödeme ve İptal */}
          <div className="mt-6 flex flex-col gap-4">
            {order?.payment_status !== 'paid' && order?.payment_status !== 'completed' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h4 className="font-bold text-amber-800 text-sm">Manuel Ödeme Onayı</h4>
                  <p className="text-xs text-amber-700">EFT/Havale ödemesi hesaba geçtiyse buradan onaylayın.</p>
                </div>
                <button
                  onClick={handleMarkAsPaid}
                  disabled={markingPaid}
                  className="whitespace-nowrap bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white font-bold py-2 px-6 rounded shadow transition-colors"
                >
                  {markingPaid ? '⏳ İşleniyor...' : '✓ Ödendi İşaretle'}
                </button>
              </div>
            )}

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-sm">
              <h4 className="font-bold text-red-800 text-sm mb-2 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Siparişi İptal Et
              </h4>
              <textarea
                className="w-full border border-red-200 rounded p-2 text-sm focus:outline-none focus:border-red-400 mb-3 placeholder-red-300"
                placeholder="Müşteriye e-posta ile iletilecek iptal nedenini buraya yazın..."
                rows="2"
              />
              <button className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded shadow transition-colors">
                İptal Et ve E-posta Gönder
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Kapat
          </Button>
        </div>
      </div>
    </div>
  );
}